import React from "react";
import { useSelector } from "react-redux";
import { Edit } from "@mui/icons-material";
import { Tooltip } from "@mui/material";

import { useGetProjectQuery, useGetVersionQuery } from "fond/api";
import AutoDesignPanel from "fond/project/panels/AutoDesignPanel";
import ExtendedDesignPanel from "fond/project/panels/ExtendedDesign/ExtendedDesignPanel";
import { ISGPanel } from "fond/project/panels/ISG";
import { MuPowerPanel } from "fond/project/panels/MuPower";
import { CostToServePanel, IDDLiteDesignPanel } from "fond/project/panels/NBN";
import SmallDesignPanel from "fond/project/panels/SmallDesign/SmallDesignPanel";
import { Store } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { Actions, permissionCheck } from "fond/utils/permissions";

import { ITabProps } from "./factory";

/**
 * Main widget component that determines the content rendered within the tab
 */
export const Widget: React.FC<ITabProps> = () => {
  const projectId = useAppSelector((state) => state.project.projectId);
  const versionId = useAppSelector((state) => state.project.versionId);
  const { data: project } = useGetProjectQuery(projectId);
  const { data: version } = useGetVersionQuery(versionId, { skip: !versionId });
  const architecture = version?.Architecture ?? null;

  // other actions within the widget that require license are locked separately
  const canEdit = useSelector(
    (state: Store) => project && permissionCheck(project.Permission.Level, Actions.PROJECT_EDIT) && project.Type !== "collaboration"
  );

  if (!project || !version) return null;

  if (project.Type === "design") {
    if (project.Subtype === "irby_small_batch") return <SmallDesignPanel project={project} readOnly={!canEdit} key={version.ID} />;
    if (project.Subtype === "nbn_cost_to_serve") return <CostToServePanel project={project} readOnly={!canEdit} key={version.ID} />;
    if (project.Subtype === "nbn_idd_lite") return <IDDLiteDesignPanel project={project} readOnly={!canEdit} key={version.ID} />;
    if (project.Subtype === "isg") return <ISGPanel project={project} readOnly={!canEdit} key={version.ID} />;
    if (project.Subtype === "mu_power") return <MuPowerPanel project={project} readOnly={!canEdit} key={version.ID} />;
  }

  if (project.Type === "planner" && project.Subtype === "planner_extended")
    return <ExtendedDesignPanel architecture={architecture} project={project} readOnly={!canEdit} key={version.ID} />;

  return <AutoDesignPanel project={project} architecture={architecture} readOnly={!canEdit} key={version.ID} />;
};

/**
 * Icon component used by the iconFactory to generate the tabs left icon adornment.
 */
export const Icon: React.FC<ITabProps> = () => (
  <Tooltip title="FOND Designer" arrow placement="right">
    <Edit />
  </Tooltip>
);
